// NovaCatering SDK - a tiny, dependency-free loader that embeds the catering app
// into any host page. It lazy-loads the <nova-catering> bundle from a CDN, mounts
// it inside a full-screen overlay, and keeps that overlay in sync with the URL.
//
// One-line integration:
//   <script src="https://<cdn>/sdk/online-catering.js"
//           data-business-ref-id="sns" data-base-path="/catering"></script>
//
// Programmatic:  NovaCatering.init({ businessRefId: 'sns' }); NovaCatering.open();

import { normalizeBase, buildCateringPath } from './cateringPath';

export interface NovaCateringConfig {
  /** Tenant/business slug (e.g. "sns"). Required. Passed to <nova-catering>. */
  businessRefId: string;
  /** Host sub-path the app lives under. Default '/catering'. */
  basePath?: string;
  /** CDN base URL that serves nova-catering.js / nova-catering.css. */
  cdnBase?: string;
  /** Id of the overlay container element. Default 'nova-catering-root'. */
  containerId?: string;
}

// Fallback CDN base - in practice this is auto-derived from the loader's own
// <script src> (see deriveCdnBase); this literal is only used if that fails.
const DEFAULT_CDN = 'https://cdnjs.lab.novatab.com/catering/v1';
const DEFAULT_BASE_PATH = '/catering';
const DEFAULT_CONTAINER_ID = 'nova-catering-root';

let cfg: Required<NovaCateringConfig> | null = null;
let assetsRequested = false;
let mounted = false;
let popstateBound = false;

const requireCfg = (): Required<NovaCateringConfig> => {
  if (!cfg) {
    throw new Error('[NovaCatering] call NovaCatering.init({ businessRefId }) first');
  }
  return cfg;
};

// Derive the bundle CDN base from the loader's own <script src>. The loader is
// served from `<cdn>/sdk/online-catering.js`, so the bundle lives at `<cdn>/v1`.
// This avoids hard-coding a CDN and the loader/bundle-origin mismatch.
const deriveCdnBase = (): string => {
  const tag =
    (document.currentScript as HTMLScriptElement | null) ||
    document.querySelector<HTMLScriptElement>('script[data-business-ref-id]');
  const src = tag?.src || '';
  const m = src.match(/^(https?:\/\/[^?#]*?)\/sdk\/[^/]+$/);
  return m ? `${m[1]}/v1` : DEFAULT_CDN;
};

// Inject the nova-catering CDN script once. Marked with data-nova-catering so
// a second SDK instance / re-init never double-loads it.
//
// The CSS bundle is deliberately NOT injected here. It's the full compiled app
// stylesheet (every Tailwind utility, e.g. `.collapse{visibility:collapse}`),
// and putting it in the HOST's own <head> applies it to the whole document —
// any host class name that happens to match a utility name (Bootstrap's own
// `.collapse`, `.container`, `.hidden`, etc.) gets silently overridden. The
// <nova-catering> element loads its own copy scoped to its Shadow Root instead
// (see nova-catering-element.tsx, `cdn-base` attribute), so it never leaks
// onto the host page.
const loadAssets = (): void => {
  if (assetsRequested) return;
  const { cdnBase } = requireCfg();
  assetsRequested = true;

  if (!document.querySelector('script[data-nova-catering]')) {
    const script = document.createElement('script');
    script.src = `${cdnBase}/nova-catering.js`;
    script.defer = true;
    script.setAttribute('data-nova-catering', '');
    document.head.appendChild(script);
  }
};

const getContainer = (): HTMLElement => {
  const { containerId } = requireCfg();
  let el = document.getElementById(containerId);
  if (!el) {
    el = document.createElement('div');
    el.id = containerId;
    // Full-viewport overlay above the host page; hidden until we're on the
    // catering path.
    el.style.cssText =
      'position:fixed;inset:0;z-index:2147483000;background:#fff;overflow:auto;display:none;';
    document.body.appendChild(el);
  }
  return el;
};

const isCateringPath = (): boolean => {
  const { basePath } = requireCfg();
  const { pathname } = window.location;
  return pathname === basePath || pathname.startsWith(`${basePath}/`);
};

// Create the <nova-catering> element once. It upgrades automatically when the CDN
// script defines the custom element, so mounting before the script loads is safe.
const mount = (): void => {
  if (mounted) return;
  const { businessRefId, basePath, cdnBase } = requireCfg();
  const el = document.createElement('nova-catering');
  el.setAttribute('business-ref-id', businessRefId);
  el.setAttribute('base-path', basePath);
  // Tells the element where to load its OWN (shadow-scoped) stylesheet from —
  // see the loadAssets() comment above for why the SDK doesn't load it itself.
  el.setAttribute('cdn-base', cdnBase);
  getContainer().appendChild(el);
  mounted = true;
};

const show = (): void => {
  getContainer().style.display = 'block';
};
const hide = (): void => {
  getContainer().style.display = 'none';
};

// Reconcile the overlay with the current URL - the single source of truth.
// Handles deep links, reloads, and browser back/forward.
const syncToUrl = (): void => {
  if (!cfg) return;
  if (isCateringPath()) {
    loadAssets();
    mount();
    show();
  } else {
    hide();
  }
};

// Host-side navigation. pushState alone won't reach react-router inside the web
// component (it listens to popstate), so we dispatch a popstate after.
const navigate = (path: string): void => {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
  syncToUrl();
};

const bindPopstate = (): void => {
  if (popstateBound) return;
  popstateBound = true;
  window.addEventListener('popstate', syncToUrl);
};

const NovaCatering = {
  /** Configure the SDK. Safe to call once; re-calling updates the config. */
  init(config: NovaCateringConfig) {
    if (!config || !config.businessRefId) {
      throw new Error('[NovaCatering] init requires a businessRefId');
    }
    cfg = {
      businessRefId: config.businessRefId,
      basePath: normalizeBase(config.basePath || DEFAULT_BASE_PATH),
      cdnBase: (config.cdnBase || deriveCdnBase()).replace(/\/+$/, ''),
      containerId: config.containerId || DEFAULT_CONTAINER_ID,
    };
    bindPopstate();
    // If the page loaded already under the catering path (deep link / refresh),
    // mount now.
    syncToUrl();
    return this;
  },

  /** Open the catering experience at the base path (e.g. "/catering"). */
  open() {
    navigate(buildCateringPath(requireCfg().basePath, 'home'));
    return this;
  },

  /** Open the catering menu at "<base-path>/menu". */
  openMenu() {
    navigate(buildCateringPath(requireCfg().basePath, 'menu'));
    return this;
  },

  /**
   * Leave the catering experience and hand the tab back to the host site.
   *
   * This has to be a real navigation rather than `history.pushState`. The
   * document underneath our overlay is the host's page for the CATERING path
   * (e.g. WordPress's own `/catering` page), not the host's home page -
   * pushState rewrites the URL bar but loads nothing, so hiding the overlay
   * just revealed that same `/catering` document now sitting at "/". Where the
   * host serves `/catering` from a blank template (as it must, so the theme
   * doesn't flash behind us) that is a blank white page.
   *
   * The overlay is deliberately left up: `assign()` commits asynchronously, so
   * hiding first would flash the bare host page before the destination paints.
   *
   * `path` defaults to the host root, overridable for hosts whose landing page
   * lives elsewhere.
   */
  close(path: string = '/') {
    requireCfg();
    window.location.assign(path);
    return this;
  },

  /** True when the current URL is inside the catering sub-path. */
  isOpen(): boolean {
    return !!cfg && isCateringPath();
  },
};

// Auto-init from the loading <script>'s data-* attributes so a single script
// line self-configures.
const autoInit = (): void => {
  const tag =
    (document.currentScript as HTMLScriptElement | null) ||
    document.querySelector<HTMLScriptElement>('script[data-business-ref-id]');
  const businessRefId = tag?.getAttribute('data-business-ref-id');
  if (!businessRefId) return;
  NovaCatering.init({
    businessRefId,
    basePath: tag?.getAttribute('data-base-path') || undefined,
    cdnBase: tag?.getAttribute('data-cdn-base') || undefined,
    containerId: tag?.getAttribute('data-container-id') || undefined,
  });
};

declare global {
  interface Window {
    NovaCatering: typeof NovaCatering;
  }
}

if (typeof window !== 'undefined') {
  window.NovaCatering = NovaCatering;
  autoInit();
}

export default NovaCatering;
